import React from 'react';
import type { FunnelInsightNodeMetric } from '../types';
import { ANALYTICS_CANVAS_TEST_IDS } from '../testIds';
import { formatPct } from '../statTierCopy';
import { unitNoun, useFunnelUnit } from '../funnelUnit';

/**
 * Canvas rework T4 (CRO-AC-006): the red down-stub that makes the per-screen
 * loss visible WHERE it happens — a dashed vertical «drain» leaving the node's
 * bottom with `−{flowLossCount} · −{rate} left here`. Same people-units as the
 * edge labels, so edges out + this stub ≈ visitors. Renders nothing without a
 * positive flow loss; the rate is omitted when the server withheld it
 * (mismatched evidence — count-only, never a fake percent). Sizes live in
 * CANVAS space (node width 430); raw heat-high literals on purpose — this is
 * the canvas components' literal palette zone.
 */

const STUB_RED = '#d32f2f'; // ANALYTICS_FRAME_COLORS.high

export interface FlowLossStubProps {
  metric: FunnelInsightNodeMetric;
  /**
   * I-A: the drain is a real affordance — click opens the drop-off audience
   * drawer. stopPropagation keeps the node-body click on the node drawer
   * (RC-AC-041); without a handler clicks bubble as before.
   */
  onFlowLossClick?: (screenId: string) => void;
}

export const FlowLossStub: React.FC<FlowLossStubProps> = ({ metric, onFlowLossClick }) => {
  const unit = useFunnelUnit();
  // F2 (FT-AC-090): the drain renders the page unit's flow delta. In people
  // mode without the person twins the stub renders NOTHING — the node card
  // carries the D7 typed-absence line for the same metric; a journey number
  // relabelled here would be the named failure (FT-AC-087).
  const flowLoss = unit === 'people'
    ? (typeof metric.visitorsPeople === 'number' ? metric.flowLossCountPeople ?? 0 : 0)
    : metric.flowLossCount ?? 0;
  if (flowLoss <= 0) return null;
  // Pre-answer zone (trait filter): everyone in the cohort survived this
  // screen by construction — a drain here would visualize survivor-bias
  // noise (back-nav artifacts), not a leak. Absence, never a fake ≈0.
  if (metric.filterObservability === 'pre') return null;
  const loss = flowLoss;
  const unitRate = unit === 'people' ? metric.flowLossRatePeople : metric.flowLossRate;
  const rate = typeof unitRate === 'number' ? formatPct(unitRate) : undefined;

  return (
    <div
      data-testid={ANALYTICS_CANVAS_TEST_IDS.flowLossStub(metric.screenId)}
      style={{ position: 'absolute', top: '100%', left: 0, right: 0, height: 96, cursor: 'pointer' }}
      onClick={onFlowLossClick ? (event) => {
        event.stopPropagation();
        onFlowLossClick(metric.screenId);
      } : undefined}
    >
      <svg width="100%" height="96" style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
        <line x1="72%" y1="6" x2="72%" y2="66" stroke={STUB_RED} strokeWidth={5} strokeDasharray="12 8" />
      </svg>
      <div
        style={{
          position: 'absolute',
          top: 64,
          left: '72%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '11px solid transparent',
          borderRight: '11px solid transparent',
          borderTop: `20px solid ${STUB_RED}`,
        }}
      />
      <span
        style={{
          position: 'absolute',
          top: 26,
          left: 'calc(72% + 14px)',
          whiteSpace: 'nowrap',
          color: STUB_RED,
          fontSize: 22,
          fontWeight: 700,
          lineHeight: 1.2,
        }}
      >
        −{loss.toLocaleString('en-US')}
        <span style={{ fontWeight: 500, fontSize: 15, color: 'rgba(128, 128, 128, 0.95)' }}>
          {/* D5: the unit noun rides the visible label on both surfaces. */}
          {rate ? ` · −${rate} ${unitNoun(unit)} left here` : ` ${unitNoun(unit)} left here`}
        </span>
      </span>
    </div>
  );
};
